import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/config';
import { getApiErrorMessage } from '../../lib/apiError';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';
import { clearLinkFromAddressBar, readLinkToken } from '../../lib/linkToken';

type Phase = 'loading' | 'subscribed' | 'unsubscribed' | 'resubscribed' | 'working' | 'invalid';

interface Preference {
  email_masked: string;
  state: 'subscribed' | 'unsubscribed';
  /** The link was already used: the page only reports where the address stands. */
  used: boolean;
  /** Single-use token, handed back by an unsubscribe, that only subscribes again. */
  undo_token: string | null;
}

/**
 * Lands the "Unsubscribe" link of promotional e-mail.
 *
 * Opening the page changes NOTHING (mail scanners and link previewers open links on their own):
 * it only asks the server where the address stands. The unsubscription is applied, at once, by
 * the button, which consumes the link (single use). The answer carries a new single-use token,
 * kept in memory only, that backs "Subscribe again" for a short time. A link that was already
 * used says so and offers nothing. No sign-in is needed: the emailed token is the credential, it
 * is stripped from the address bar before any request and travels in a POST body. Only
 * promotional e-mail is affected.
 */
export default function EmailPreferences(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading');
  const [address, setAddress] = useState('');
  const [message, setMessage] = useState('');
  const [used, setUsed] = useState(false);
  const [undoToken, setUndoToken] = useState<string | null>(null);
  const token = useRef('');
  const started = useRef(false);

  const show = useCallback((pref: Preference) => {
    setAddress(pref.email_masked);
    setUsed(pref.used);
    setUndoToken(pref.undo_token);
    setMessage('');
    setPhase(pref.state);
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    token.current = readLinkToken();
    clearLinkFromAddressBar();
    if (!token.current) {
      setPhase('invalid');
      setMessage('This link is incomplete. To stop promotional e-mail, write to support.');
      return;
    }
    axios
      .post<Preference>(`${API_URL}/api/v1/email-preferences/inspect`, { token: token.current })
      .then((res) => show(res.data))
      .catch((err: unknown) => {
        setPhase('invalid');
        setMessage(
          getApiErrorMessage(err, 'This link is not valid. To stop promotional e-mail, write to support.'),
        );
      });
  }, [show]);

  const change = useCallback(
    async (action: 'unsubscribe' | 'subscribe') => {
      const sent = action === 'subscribe' ? undoToken : token.current;
      setPhase('working');
      try {
        const res = await axios.post<Preference>(
          `${API_URL}/api/v1/email-preferences/${action}`,
          { token: sent },
        );
        show(res.data);
        if (action === 'subscribe' && !res.data.used) setPhase('resubscribed');
      } catch (err: unknown) {
        setMessage(getApiErrorMessage(err, 'We could not save your choice. Please write to support.'));
        // The undo token is spent or gone either way; the unsubscription itself stands.
        setUndoToken(null);
        setPhase(action === 'subscribe' ? 'unsubscribed' : 'invalid');
      }
    },
    [show, undoToken],
  );

  const heading = {
    loading: 'One moment…',
    subscribed: 'Promotional e-mail',
    unsubscribed: 'You are unsubscribed',
    resubscribed: 'You are subscribed again',
    working: 'One moment…',
    invalid: 'We could not open this link',
  }[phase];

  const buttonStyle = {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
    color: NAVY,
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})` }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h1 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>
          {heading}
        </h1>

        {phase === 'subscribed' && (
          <>
            <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
              You are receiving promotional e-mail at:
            </p>
            <p className="text-sm font-semibold mb-4" style={{ color: '#FFFFFF' }} translate="no">
              {address}
            </p>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Messages about your account and your requests will still reach you.
            </p>
            {used ? (
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                This link was already used.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => void change('unsubscribe')}
                className="w-full rounded-xl px-6 py-3 text-sm font-semibold"
                style={buttonStyle}
              >
                Unsubscribe
              </button>
            )}
          </>
        )}

        {phase === 'unsubscribed' && (
          <>
            <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
              We will not send promotional e-mail to:
            </p>
            <p className="text-sm font-semibold mb-4" style={{ color: '#FFFFFF' }} translate="no">
              {address}
            </p>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Messages about your account and your requests will still reach you.
            </p>
            {used && (
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                This link was already used. This address is already unsubscribed.
              </p>
            )}
            {message !== '' && (
              <p className="text-sm leading-relaxed" role="alert" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {message}
              </p>
            )}
            {undoToken !== null && (
              <button
                type="button"
                onClick={() => void change('subscribe')}
                className="w-full rounded-xl px-6 py-3 text-sm font-semibold"
                style={buttonStyle}
              >
                Subscribe again
              </button>
            )}
          </>
        )}

        {phase === 'resubscribed' && (
          <>
            <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
              You will receive promotional e-mail at:
            </p>
            <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }} translate="no">
              {address}
            </p>
          </>
        )}

        {phase === 'invalid' && (
          <p className="text-sm leading-relaxed" role="alert" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
